// @ts-check

import { defineCustomElement as defineVueCustomElement } from "vue";
import { MARKER_PROPS, withMarkerCleanupHook, withMountReadyHook } from "./mount-ready";
import { registerCustomElement } from "./registration";
import { componentFileNameFromPath, enrichProps, extractComponentName, isFormAssociated } from "./utils";

// Glob-free by design: the eager catalog glob lives in framework/eager.js and
// the lazy catalog glob in framework/catalog.js. Anything imported from here
// is safe in both build graphs.

/**
 * Registers a component.
 *
 * @param {{ default: import("vue").DefineComponent }} component - The component module (eager glob entry).
 * @param {string} path - The path to the component.
 * @param {Record<string, string[]>} aliases - Aliases for the component names.
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 */
export function registerComponent(component, path, aliases, props) {
  const fileName = componentFileNameFromPath(path);

  const elementConstructor = defineCustomElement(component.default, fileName, props);
  registerCustomElement(fileName, elementConstructor, { formAssociated: isFormAssociated(component.default) });

  const componentName = extractComponentName(fileName);
  if (componentName in aliases) {
    registerAliases(aliases[componentName], component, props);
  }
}

/**
 * Registers the aliases for the component.
 *
 * @param {string[]} aliases - Aliases for the component.
 * @param {{ default: import("vue").DefineComponent }} component - The component module (eager glob entry).
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 */
function registerAliases(aliases, component, props) {
  aliases.forEach((alias) => registerAlias(alias, component, props));
}

/**
 * Registers an alias for the component.
 *
 * @param {string} alias - The alias for the component.
 * @param {{ default: import("vue").DefineComponent }} component - The component module (eager glob entry).
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 */
function registerAlias(alias, component, props) {
  const elementConstructor = defineCustomElement(component.default, alias, props);
  registerCustomElement(alias, elementConstructor, { formAssociated: isFormAssociated(component.default) });
}

/**
 * Prepares a component definition for registration as a custom element:
 * enriches the props with the deployment defaults, merges the mount-ready
 * hook and turns Vue's attribute fallthrough off unless the component sets
 * `inheritAttrs` itself. Vue hands every host attribute to the component, and
 * the attribute already sits on the host, so fallthrough would copy it onto
 * the root inside the shadow root (a second `id`, `class` or `style`), or warn
 * when that root is a fragment. A component that forwards attributes inward
 * binds them itself. This is the shared def preparation both builds use — the
 * eager path passes the result to `defineVueCustomElement` directly, the lazy
 * loader resolves it through `defineAsyncComponent`.
 *
 * @param {import("vue").DefineComponent} component
 * @param {string} fileName
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 * @returns {import("vue").DefineComponent}
 */
export function prepareComponentDef(component, fileName, props) {
  return {
    ...component,
    inheritAttrs: component.inheritAttrs ?? false,
    props: { ...enrichProps(component, fileName, props), ...MARKER_PROPS },
    mounted: withMountReadyHook(component),
    beforeUnmount: withMarkerCleanupHook(component),
  };
}

/**
 * Defines a custom element.
 * If applicable, the props passed to the custom element will be used as the default values for the component props.
 *
 * @param {import("vue").DefineComponent} component
 * @param {string} fileName
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 * @returns {import("vue").VueElementConstructor<{}>}
 */
export function defineCustomElement(component, fileName, props) {
  // @ts-ignore
  return defineVueCustomElement(prepareComponentDef(component, fileName, props));
}
